<template>
   <common-form
      class='plan-form'
      :form-title="formTitle"
      :back-route="{name: 'home'}"
      :errors="errors.non_field_errors"
      @submit="save"
      @save-and-continue="saveAndContinue"
      >
         <div class='inline-form-group'>
            <input-field :value="plan.state" @input="selectState" type="select" label="Province" :options="stateOptions" :errors="errors.state" required/>
            <input-field :value="plan.district" @input="selectDistrict" type="select" label="District" :options="districtOptions" :errors="errors.district" required/>
            <input-field :value="plan.municipality" @input="selectMunicipality" type="select" label="Municipality" :options="municipalityOptions" :errors="errors.municipality" required/>
         </div>
         <div class='inline-form-group'>
            <input-field v-model="plan.starting_year" @input="suggestName" type="year" label="From year" :errors="errors.starting_year" required/>
            <input-field v-model="plan.ending_year" @input="suggestName" type="year" label="To year" :errors="errors.ending_year" required/>
         </div>
         <input-field v-model="plan.name" type="text" label="Name of the plan" :errors="errors.name" required/>
         <div class="person-form-group">
            <div class='title'>Mayor/Chairperson</div>
            <div class="inline-form-group">
               <input-field v-model="plan.mayor.name" type="text" label="Name" :errors="errors.mayor.name" required/>
               <input-field v-model="plan.mayor.contact_no" type="text" label="Contact" :errors="errors.mayor.contact_no" required/>
            </div>
            <input-field v-model="plan.mayor.email" type="email" label="Email" :errors="errors.mayor.email"/>
         </div>
         <div class="person-form-group">
            <div class='title'>Deputy Mayor/Vice Chairperson</div>
            <div class="inline-form-group">
               <input-field v-model="plan.deputy_mayor.name" type="text" label="Name" :errors="errors.deputy_mayor.name" required/>
               <input-field v-model="plan.deputy_mayor.contact_no" type="text" label="Contact" :errors="errors.deputy_mayor.contact_no" required/>
            </div>
            <input-field v-model="plan.deputy_mayor.email" type="email" label="Email" :errors="errors.deputy_mayor.email"/>
         </div>
         <div class="person-form-group">
            <div style="margin: 2px">Ward Chairpersons</div>
            <small>Edit the name, email and contact no columns in the table as necessary</small>
            <errors :errors="wardOfficerErrors"/>
            <!-- key for the vue-editable-grid is set to to plan.ward_officers.length so that it re-renders when the length changes -->
            <!-- and components re-renders when their keys are changed -->
            <vue-editable-grid
               ref="wardTable"
               id="ward-table"
               class='ward-table'
               :key="plan.ward_officers.length"
               :column-defs="wardColumnDefs"
               :row-data="plan.ward_officers"
               row-data-key="ward_no"
               :enable-filters="false"
               :item-height="35"
            />
         </div>
         <input-field v-model="plan.address" type="text" label="Address" :errors="errors.address" required/>
         <input-field v-model="plan.email" type="email" label="Municipality email" :errors="errors.email"/>
         <input-field v-model="plan.area_sqkm" type="number" label="Area (sq. km)" :extra-attributes="{step: 0.0001, min: 0}" :errors="errors.area_sqkm" required/>
         <div class="neighbours">
            <div class='title'>Neighbouring Municipalities</div>
            <div class='neighbour-item' v-for="({sddmm, name}) in neighbouringMunicipalities" :key="sddmm">
               &bull; {{ name }} - ({{sddmm}})
            </div>
         </div>
   </common-form>
</template>

<script>
import CommonForm from '@/components/organisms/Home/Plan/Form';
import InputField from '@/components/molecules/Login/InputField';
import Errors from '@/components/molecules/Login/Errors';
import {default as axios, postWithAuthToken} from '@/axios';
import { updateProps, getDefaultErrors } from '@/js/form-helpers';
import { showError, showSuccess } from '@/js/ui-helpers';
import { mapActions, mapGetters } from 'vuex';
import { selectMunicipalityFromTop } from '@/js/mapNavigator';
import { fetchWithAuthToken } from '@/axios';

export default {
   name: "plan-form",
   components: {
      InputField,
      CommonForm,
      Errors,
   },
   data()
   {
      const plan = {
         id: null,
         plan_id: "",
         state: "",
         district: "",
         municipality: "",
         name: "",
         starting_year: "",
         ending_year: "",
         mayor: {name: "", contact_no: "", email: "", designation: "Mayor/Chairperson"},
         deputy_mayor: {name: "", contact_no: "", email: "", designation: "Deputy Mayor/Vice Chairperson"},
         ward_officers: [],
         address: "",
         email: "",
         area_sqkm: "",
         neighbouring_muni_ids: [],
      };
      const wardColumnDefs = [
         { size: '7ch', sortable: true, field: 'ward_no',    headerName: 'Ward No', type: 'numeric', },
         { size: '1fr', sortable: true, field: 'name',       headerName: 'Name', maxlength: 30, editable: true },
         { size: '1fr', sortable: true, field: 'email',      headerName: 'Email', editable: true },
         { size: '12ch', sortable: true, field: 'contact_no', headerName: 'Contact No', editable: true },
      ];
      const errors = getDefaultErrors(plan);
      return {
         plan: plan,
         errors: errors,
         wardColumnDefs,
         stateOptions: [],
         allDistricts: [],
         allMunicipalities: [],
         wardOfficerErrors: [],
      };
   },
   computed: {
      ...mapGetters('plan', ['getPlan']),
      isEditForm()
      {
         return this.$route.name==='edit-plan';
      },
      formTitle()
      {
         if (this.isEditForm)
         {
            return `Edit Plan (${this.getPlanId()})`;
         }
         return "Create a new plan"
      },
      districtOptions()
      {
         if (this.plan.state==="")
         {
            return [{title: '-', value: ''}];
         }
         return this.allDistricts.filter(item => item.scode===this.plan.state);
      },
      municipalityOptions()
      {
         if (this.plan.district==="")
         {
            return [{title: '-', value: ''}];
         }
         return this.allMunicipalities.filter(item => item.dcode===this.plan.district);
      },
   },
   asyncComputed: {
      neighbouringMunicipalities:
      {
         async get ()
         {
            if (!this.plan.municipality) return [];
            // call the api here
            const {data: {neighbours}} = await axios.get(`api/layers/neighbour-municipalities/${this.plan.municipality}`);
            this.plan.neighbouring_muni_ids = neighbours.map(neighbour => neighbour.sddmm)
            return neighbours;
         },
      },
   },
   created()
   {
      fetchWithAuthToken('api/layers/state-options').then(({data}) =>
      {
         this.stateOptions = data;
      });
      fetchWithAuthToken('api/layers/district-options').then(({data}) =>
      {
         this.allDistricts = data;
      });
      fetchWithAuthToken('api/layers/municipality-options').then(({data}) =>
      {
         this.allMunicipalities = data;
      });
      // move this to store

      if (this.isEditForm)
      {
         this.fetchPlan(this.getPlanId())
         .then(plan =>
         {
            // format plan from api to this.plan's format
            const sddmm =plan.municipality.sddmm.toString();
            plan.state= parseInt(sddmm[0]);
            plan.district= parseInt(sddmm.slice(1,3));
            plan.municipality= parseInt(sddmm);
            plan.starting_year= new Date(plan.starting_year);
            plan.ending_year= new Date(plan.ending_year);
            updateProps(this.plan, plan);
            selectMunicipalityFromTop(plan.municipality);
         })
         .then(() => this.updateWardOfficers())
      }
   },
   methods: {
      ...mapActions('plan', ['fetchPlan']),
      suggestName()
      {
         if (this.plan.municipality)
         {
            const municipality = this.allMunicipalities.find(muni => muni.value===this.plan.municipality);
            const startingYear = this.plan.starting_year && this.plan.starting_year.getFullYear();
            const endingYear   = this.plan.ending_year && this.plan.ending_year.getFullYear();
            this.plan.name = `${municipality.title} ${municipality.type} Energy Plan ${startingYear}-${endingYear}`;
         }
      },
      selectState(newValue)
      {
         this.plan.state = newValue;
         this.plan.district = '';
         this.plan.municipality = '';
         this.suggestName();
      },
      selectDistrict(newValue)
      {
         this.plan.district = newValue;
         this.plan.municipality = '';
         this.suggestName();
      },
      selectMunicipality(newValue)
      {
         this.plan.municipality = newValue;
         this.suggestName();
         this.updateMunicipalityArea();
         this.updateWardOfficers();
      },
      updateMunicipalityArea()
      {
         fetchWithAuthToken(`api/layers/municipality-area/${this.plan.municipality}`)
            .then(({data: area}) => this.plan.area_sqkm=area)
      },
      getPlanId()
      {
         console.assert(this.isEditForm, "invoking plan id getter in create form");
         return this.plan.id || this.$route.params.id;
      },
      getFormatedPlanForPost()
      {
         const formatDate = date => {
            var tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds;
            return (new Date(date - tzoffset)).toISOString().slice(0,'YYYY-MM-DD'.length);
         };
         const created_by = this.plan.created_by_id;
         const municipality_officers = [this.plan.mayor, this.plan.deputy_mayor];
         const planCopy = JSON.parse(JSON.stringify(this.plan));
         delete planCopy['mayor'];
         delete planCopy['deputy_mayor'];
         return {
            ...planCopy,
            created_by,
            municipality_officers,
            starting_year: formatDate(this.plan.starting_year),
            ending_year: formatDate(this.plan.ending_year),
         };
      },
      getPostOptions()
      {
         if (this.isEditForm)
            return {
               url: `api/plan/${this.getPlanId()}`,
               method: 'put',
               successMsg: 'Successfully edited the project',
               errorMsg: 'Failed to edit the project',
            };

         return {
            url: 'api/plan/',
            method: 'post',
            successMsg: 'Successfully created a new project',
            errorMsg: 'Failed to create a project',
         };
      },
      async updateWardOfficers()
      {
         const { data: officers } = await fetchWithAuthToken(`api/plan/wardofficers/${this.plan.municipality}?plan_id=${this.isEditForm ? this.getPlanId() : -1}`);
         this.plan.ward_officers = officers;
      },
      formatPlanForPost()
      {
         return {};
      },
      async save(form, {goToNextStep=false}={})
      {
         updateProps(this.errors, getDefaultErrors(this.plan))
         if (!form.checkValidity())
         {
            form.reportValidity();
            return;
         }
         try
         {
            const { url,method,successMsg } = this.getPostOptions();
            console.log(this.getFormatedPlanForPost());
            const results = await postWithAuthToken(url, this.getFormatedPlanForPost(), {method});
            if (results)
            {
               const params = {id: results.data.id};
               if (goToNextStep)
               {
                  this.$router.push({name: 'edit-demography-info', params})
               }
               else if (this.$route.name!=='edit-plan')
               {
                  this.$router.push({name: 'edit-plan', params });
               }
               showSuccess('Plan Form', successMsg);
               this.fetchPlan(this.getPlanId()).then(plan => {
                  selectMunicipalityFromTop(plan.municipality.sddmm);
               });
            }
         }
         catch (error)
         {
            const { errorMsg } = this.getPostOptions();
            showError('Plan Form', `${errorMsg}`);
            const errors = error.response && error.response.data;
            if (errors)
            {
               updateProps(this.errors, errors);
               if (errors.ward_officers)
               {
                  this.wardOfficerErrors = errors.ward_officers.reduce((acc, error, index) =>
                  {
                     const combinedError = Object.entries(error).map(([key,val]) => `Ward ${index+1}: ${key} field -> ${val}`);
                     return [...acc, ...combinedError];
                  }, []);
               }
            }
            else
            {
               console.error(error, errors, error.response);
            }
         }
      },
      saveAndContinue(form)
      {
         this.save(form, {goToNextStep: true})
      },
   },
}
</script>

<style lang="scss">
.plan-form {
   .person-form-group  {
      background: rgba(black,0.04);
      border-radius: 0.2em;
      margin-top: 0.3em;
      margin-bottom: 0.9em;
      padding: 0.3em;

      > .title {
         font-family: $primary-font;
         color: $font-color;
         margin-bottom: -0.8em;
         font-weight: 500;
         font-size: 0.9em;
      }

   }
   .neighbours {
      > .title {
         font-size: 0.93em;
      }
      > .neighbour-item {
         font-size: 0.9em;
         line-height: 1.8em;
         margin-left: 0.5em;
      }
   }
}
</style>
